<template>
    <div class="hris-profile w-full max-w-sm">
        <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 body-small w-full">
            <!-- Editable name -->
            <dt class="font-medium text-control-label">{{ t("onboarding.hrisProfile.nameLabel") }}</dt>
            <dd>
                <BaseButton v-if="!editingName" theme="tertiary" size="sm" class="group text-left" @click="startEditingName">
                    {{ name || "—" }}
                    <i class="bi bi-pencil text-xs text-ink-muted opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true"></i>
                </BaseButton>
                <input
                    v-else
                    ref="nameInputRef"
                    :value="name"
                    :aria-label="t('onboarding.personalization.nameLabel')"
                    autocomplete="name"
                    type="text"
                    class="inline-edit-input"
                    :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'"
                    @input="$emit('update:name', ($event.target as HTMLInputElement).value)"
                    @blur="editingName = false"
                    @keydown.enter="finishEditingName" />
            </dd>

            <!-- Editable role -->
            <dt class="font-medium text-control-label">{{ t("onboarding.hrisProfile.roleLabel") }}</dt>
            <dd>
                <BaseButton v-if="!editingRole" theme="tertiary" size="sm" class="group text-left" @click="startEditingRole">
                    {{ role || "—" }}
                    <i class="bi bi-pencil text-xs text-ink-muted opacity-0 transition-opacity group-hover:opacity-100" aria-hidden="true"></i>
                </BaseButton>
                <input
                    v-else
                    ref="roleInputRef"
                    :value="role"
                    :aria-label="t('onboarding.personalization.roleLabel')"
                    autocomplete="organization-title"
                    type="text"
                    class="inline-edit-input"
                    :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'"
                    @input="$emit('update:role', ($event.target as HTMLInputElement).value)"
                    @blur="editingRole = false"
                    @keydown.enter="finishEditingRole" />
            </dd>

            <!-- Read-only HRIS fields -->
            <template v-for="field in readOnlyFields" :key="field.key">
                <dt class="font-medium text-control-label">{{ field.label }}</dt>
                <dd class="text-ink-muted">{{ field.value }}</dd>
            </template>
        </dl>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { unlessComposing } from "~vue/utils/keyboard";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = withDefaults(
    defineProps<{
        name?: string;
        role?: string;
        hrisData: { manager?: string | null; department?: string | null; hire_date?: string | null };
    }>(),
    { name: "", role: "" },
);

defineEmits<{ "update:name": [value: string]; "update:role": [value: string] }>();

const editingName = ref(false);
const editingRole = ref(false);
const finishEditingName = unlessComposing(() => (editingName.value = false));
const finishEditingRole = unlessComposing(() => (editingRole.value = false));
const nameInputRef = ref<HTMLInputElement | null>(null);
const roleInputRef = ref<HTMLInputElement | null>(null);

function startEditingName() {
    editingName.value = true;
    nextTick(() => nameInputRef.value?.focus());
}

function startEditingRole() {
    editingRole.value = true;
    nextTick(() => roleInputRef.value?.focus());
}

const readOnlyFields = computed(() => {
    const d = props.hrisData;
    return [
        { key: "manager", label: t("onboarding.hrisProfile.managerLabel"), value: d.manager },
        { key: "department", label: t("onboarding.hrisProfile.teamLabel"), value: d.department },
        { key: "hire_date", label: t("onboarding.hrisProfile.startDateLabel"), value: d.hire_date },
    ].filter((f) => f.value);
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .hris-profile {
        @apply rounded-2xl border border-border bg-surface-raised p-6 flex flex-col items-center;
    }

    .inline-edit-input {
        @apply w-full rounded-md border border-control-border bg-control-bg px-2 py-1 body-small text-control-text-strong;
    }
}
</style>
